import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData, getTablebySql } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, useMenuStore } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import { showPopupModal } from "@widgets/CommonModal";
import ResetPwdPopup from "@widgets/commonpopup/ResetPwdPopup";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_SYS_AUT_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid: [],
  });

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    try {
      const modifiedRows = gridRefs.current["grid"].current.getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const result = await onSave();
          if (!result) return;
        }
      }

      const params = {
        id: "sn_sys_aut_reg_004_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      if (data) {
        setDataSources({
          grid: data[0],
        });
      } else {
        setDataSources({
          grid: [],
        });
      }
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_dept: userInfo.cd_dept,
        yn_gpmenu: "N",
        yn_use: "Y",
        fg_user: "1",
      };
      gridRefs.current["grid"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid"].current.getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }
    gridRefs.current["grid"].current?.deleteSelectedRows();
    gridRefs.current["grid"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid"].current.resetUpdate();
      return;
    }

    await onSave();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const changesRow = gridRefs.current["grid"].current.getChange();
      if (changesRow.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const insertList = changesRow.insert || [];
      for (const row of insertList) {
        if (!row.no_pwd || row.no_pwd.trim() === "") {
          await showDialog("alter", "비밀번호는 필수값입니다.");
          return false;
        }
      }

      const saveData = {
        sys_user: changesRow,
      };
      const ok = await fnSave("SN_SYS_AUT_REG_004_SAVE_01", saveData);
      if (!ok) return;

      // 성공적으로 저장된 후 초기화
      gridRefs.current["grid"].current.forEachFilteredNode((node: RowNode) => {
        if (node.data.no_pwd) node.data.no_pwd = "";
      });
      gridRefs.current["grid"].current.resetUpdate();

      return true;
    } catch (error: any) {
      gridRefs.current["grid"].current.resetUpdate(true);
      return false;
    }
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    if (colId === "cd_emp") {
      const sqlQuery2 = {
        sql: "select cd_gp from mas_emp where cd_firm = :cd_firm and cd_emp = :cd_emp",
        cd_firm: userInfo.cd_firm,
        cd_emp: rowData.cd_emp,
      };
      const qt_inv = await getTablebySql(sqlQuery2);
      if (qt_inv.length === 0) {
        rowNode.setDataValue("cd_gp", "");
      } else if (qt_inv.length > 0) {
        rowNode.setDataValue("cd_gp", qt_inv[0]["cd_gp"]);
      }
      if (
        rowData.cd_user === "" ||
        rowData.cd_user === null ||
        rowData.cd_user === undefined
      ) {
        rowNode.setDataValue("cd_user", rowData.cd_emp);
      }
      if (
        rowData.nm_user === "" ||
        rowData.nm_user === null ||
        rowData.nm_user === undefined
      ) {
        rowNode.setDataValue("nm_user", rowData.nm_emp);
      }
    } else if (colId === "yn_gpmenu") {
      if (rowData.yn_gpmenu === "N") {
        rowNode.setDataValue("cd_gpmenu", "");
        rowNode.setDataValue("nm_gpmenu", "");
      }
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id === "btnPassword") {
      const selectedRows = gridRefs.current["grid"].current.getSelectedRows();
      if (!selectedRows) {
        await showDialog("alter", "선택된 사용자가 없습니다.");
        return;
      }

      const selectedRow = selectedRows[0];
      if (!selectedRow) {
        await showDialog("alter", "선택된 사용자가 없습니다.");
        return;
      }

      const changesRow = gridRefs.current["grid"].current.getChange();
      const insertList = changesRow.insert || [];
      if (insertList.length > 0) {
        await showDialog(
          "alter",
          "신규 행 저장 후 비밀번호 변경을 진행해주시기 바랍니다."
        );
        return;
      }

      const component = <ResetPwdPopup cd_user={selectedRow.cd_user} />;
      const result = await showPopupModal({
        title: "비밀번호 변경",
        children: component,
        width: "350px",
        height: "275px",
      });
    }
  };

  const grids = useMemo(
    () => ({
      grid: {
        columns: columns.grid,
        dataSource: dataSources.grid,
        events: {
          onCellValueChanged: onCellValueChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          
        />
      </div>

      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnGrid
          ref={gridRefs.current["grid"]}
          columnConfig={grids.grid.columns}
          rowData={grids.grid.dataSource}
          etcProps={{
            rowSelection: "single",
            title: "상세정보",
          }}
          events={grids.grid.events}
          header={
            <div>
              <SnButton
                text={"비밀번호 변경"}
                onClick={() => {
                  onGrid1BtnsClick("btnPassword");
                }}
              />
            </div>
          }
        />
      </div>
    </div>
  );
});
export default SN_SYS_AUT_REG_004;
